/*
 * CUSTOM: Size finder recommendation on the product page
 * Reads the size determined by the MYMARINI size finder (localStorage "mm-size-profile")
 * and applies it to the main product's size option: marks the recommended swatch with a
 * dot, auto-preselects it when available, and fills a confirmation line under the picker.
 *
 * Second line (added 2026-08-06): when there is NO usable recommendation, the confirmation
 * line stays empty and a prompt line offers the size finder instead ("Nicht sicher, welche
 * Größe passt? Größe finden"). Exactly one of the two lines is ever visible.
 *
 * The variant-picker block is swapped on every partial rerender (theme.js onRerender_fn,
 * element.replaceWith), so this controller lives globally (loaded on the PDP) and re-applies
 * on each relevant event instead of running inline in the swapped markup. `reconcile`
 * (marker + line) is idempotent and never changes the selection; `maybePreselect`
 * (input.click) is strictly separated and guarded so it can never cause a
 * preselect -> rerender -> reconnect -> preselect loop.
 *
 * No-flash rule: while an auto-preselect is still pending, reconcile keeps the hint hidden.
 * The line only appears once the final state is reached (after the preselect rerender), so
 * the customer never sees the "neutral" line flip to the "preselected" line. The prompt line
 * follows the same rule: it stays hidden whenever a recommendation exists, pending or not.
 *
 * Quick-buy support (added 2026-08-22): the hint also renders inside the quick-buy modal
 * (context "quick_buy", see product-info.liquid). Each hint resolves ITS OWN picker —
 * inside a <quick-buy-modal> the modal's variant-picker[context="quick_buy"], otherwise the
 * main product picker. Modal content is injected fresh on every open (and cleared on close),
 * so dialog:before-show applies marker/preselect/line; dialog:after-hide releases the
 * one-shot preselect guard for the modal's product so the recommendation preselects again
 * on the next open. product:rerender is caught ONCE at document in the capture phase (the
 * event does not bubble, but capture still passes through document) — this survives the
 * quick-buy's FULL content replacement per variant change (its <product-rerender> has no
 * allow-partial-rerender, so the form element itself is swapped; a form-bound listener died
 * after the first change).
 */
(() => {
  if (window.__mmSizeFinderInit) return;
  window.__mmSizeFinderInit = true;

  const PROFILE_KEY = 'mm-size-profile';
  const HINT_SELECTOR = '.custom-size-finder-hint';
  const PROMPT_SELECTOR = '.custom-size-finder-prompt';
  const PICKER_SELECTOR = 'variant-picker[context="main_product"]';
  const MARKER_CLASS = 'custom-size-finder-recommended';
  // The theme's size guide trigger inside the variant picker (opens x-modal[id^="size-chart-"]).
  const SIZE_GUIDE_TRIGGER_SELECTOR = 'button.link[aria-controls^="size-chart"]';

  // Product handles we have already auto-preselected (one-shot per page load) and handles
  // where the customer manually chose a size (never auto-preselect over a manual choice).
  // Both are cleared on a fresh quiz result so the new size preselects again.
  const preselectedHandles = new Set();
  const touchedHandles = new Set();

  // Signature of the profile we last acted on, so we only re-preselect on a genuinely new quiz
  // result (not when the size guide is merely opened and closed without a new answer).
  let lastProfileRaw = null;

  const norm = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();

  const eachHint = (fn) => document.querySelectorAll(HINT_SELECTOR).forEach(fn);

  const eachPrompt = (fn) => document.querySelectorAll(PROMPT_SELECTOR).forEach(fn);

  // Each hint/prompt owns its picker: inside the quick-buy modal the modal's own picker,
  // otherwise the main product picker. The modal is moved to document.body while open,
  // so closest() keeps working there.
  const getPickerFor = (el) => {
    const modal = el.closest('quick-buy-modal');
    if (modal) return modal.querySelector('variant-picker[context="quick_buy"]');
    return document.querySelector(PICKER_SELECTOR);
  };

  const readProfileRaw = () => {
    try {
      return localStorage.getItem(PROFILE_KEY);
    } catch (error) {
      return null;
    }
  };

  // Target size (raw display value) for a hint's product type, or '' when no valid profile.
  const readTargetSize = (key) => {
    const raw = readProfileRaw();
    if (!raw) return '';

    let profile;
    try {
      profile = JSON.parse(raw);
    } catch (error) {
      return '';
    }
    if (!profile || profile.v !== 1 || !profile.result) return '';

    const result = profile.result;
    // 'set' (Bikini Set) uses the common swim size — no split display.
    const map = {
      top: result.top,
      bottom: result.bottom,
      swim: result.swim,
      resort: result.resort,
      onesize: result.onesize,
      set: result.swim,
    };
    const value = map[key];
    return value ? String(value) : '';
  };

  // Locate the recommended size radio + its label within the main product's size fieldset.
  // Scoped strictly to the size option position, so color swatches are never touched.
  // Works for both the hint and the prompt element — they carry the same data attributes.
  const findRecommended = (hint, picker) => {
    const position = hint.dataset.mmSizeOptionPosition;
    if (!position) return null;

    const target = norm(readTargetSize(hint.dataset.mmSizeKey));
    if (!target) return null;

    const formId = hint.dataset.mmFormId;
    let selector = `input[data-option-position="${position}"]`;
    if (formId) selector += `[form="${formId}"]`;

    const radios = picker.querySelectorAll(selector);
    for (const radio of radios) {
      const label = radio.labels && radio.labels[0];
      if (!label) continue;
      if (norm(label.textContent) === target) return { radio, label };
    }
    return null;
  };

  // A programmatic radio.click() OUTSIDE the topmost open overlay is treated by that
  // overlay's focus trap as an outside click and CLOSES it (symptom: "See my estimate"
  // closed the size-chart popup, because the new recommendation preselected a size behind
  // the modal). While such an overlay is open, the preselect is therefore not pending —
  // reconcile then shows the neutral line, and the click is caught up once the overlay is
  // closed (size-chart close -> handleProfileMaybeChanged, quick-buy close -> applyAll).
  const preselectBlockedByOverlay = (radio) => {
    const overlays = document.querySelectorAll('x-modal[open], quick-buy-modal[open], cart-drawer[open]');
    const topmost = overlays.length ? overlays[overlays.length - 1] : null;
    return !!(topmost && !topmost.contains(radio));
  };

  // True when an auto-preselect for this hit is still outstanding (available, not yet selected,
  // no blocking guard). Used both to trigger the click and to keep the hint hidden until done.
  const isPreselectPending = (hint, hit) => {
    const handle = hint.dataset.mmProductHandle || '';
    if (preselectedHandles.has(handle) || touchedHandles.has(handle)) return false;
    if (hit.radio.checked) return false;
    if (hit.radio.disabled || hit.label.classList.contains('is-disabled')) return false;
    if (preselectBlockedByOverlay(hit.radio)) return false;
    return true;
  };

  const fillLine = (hint, hit) => {
    const textEl = hint.querySelector('.custom-size-finder-hint__text');
    if (!textEl) return;

    const soldOut = hit.radio.disabled || hit.label.classList.contains('is-disabled');
    let template;
    if (soldOut) {
      template = hint.dataset.mmTextSoldout;
    } else if (hit.radio.checked) {
      template = hint.dataset.mmTextPreselected;
    } else {
      template = hint.dataset.mmTextNeutral;
    }

    const displaySize = hit.label.textContent.trim();
    textEl.textContent = (template || '').replace('{size}', displaySize);
  };

  // Idempotent: (re)set marker + line to match the current DOM/profile state. Never changes
  // the selection. Keeps the hint hidden while a preselect is still pending (no-flash rule).
  const reconcile = (hint) => {
    const picker = getPickerFor(hint);
    if (!picker) return;

    picker.querySelectorAll('.' + MARKER_CLASS).forEach((el) => el.classList.remove(MARKER_CLASS));

    const hit = findRecommended(hint, picker);
    if (!hit) {
      hint.hidden = true;
      return;
    }

    hit.label.classList.add(MARKER_CLASS);

    // Preselect about to happen -> stay hidden; the post-rerender reconcile shows the final line.
    if (isPreselectPending(hint, hit)) {
      hint.hidden = true;
      return;
    }

    fillLine(hint, hit);
    hint.hidden = false;
  };

  const findSizeGuideTrigger = (picker) => picker.querySelector(SIZE_GUIDE_TRIGGER_SELECTOR);

  // The size guide popup is a Shopify page assigned per template. Only some of them contain
  // the size finder quiz (templates with an empty or legacy size_chart_page do not), so the
  // prompt must never invite customers into a popup that cannot answer it.
  const sizeGuideHasFinder = (trigger) => {
    const modalId = trigger.getAttribute('aria-controls');
    if (!modalId) return false;
    const modal = document.getElementById(modalId);
    return !!(modal && modal.querySelector('#mm-sizefinder'));
  };

  // Idempotent, mirrors reconcile(): the prompt is the fallback for "no recommendation to show".
  const reconcilePrompt = (prompt) => {
    const picker = getPickerFor(prompt);
    if (!picker) {
      prompt.hidden = true;
      return;
    }

    // A recommendation exists (or is about to be applied) -> the hint owns this line.
    if (findRecommended(prompt, picker)) {
      prompt.hidden = true;
      return;
    }

    const trigger = findSizeGuideTrigger(picker);
    prompt.hidden = !(trigger && sizeGuideHasFinder(trigger));
  };

  // Auto-select the recommended size — only on init and after a fresh quiz result.
  const maybePreselect = (hint) => {
    const picker = getPickerFor(hint);
    if (!picker) return;

    const hit = findRecommended(hint, picker);
    if (!hit) return;

    const handle = hint.dataset.mmProductHandle || '';
    if (!isPreselectPending(hint, hit)) {
      // Already selected -> remember so we don't keep re-checking. Sold out/touched -> leave as is.
      if (hit.radio.checked) preselectedHandles.add(handle);
      return;
    }

    // Mark before clicking so the click-triggered rerender/reconnect cannot re-enter here.
    preselectedHandles.add(handle);
    hit.radio.click();
  };

  const apply = (hint) => {
    reconcile(hint);
    maybePreselect(hint);
  };

  const reconcileAll = () => requestAnimationFrame(() => {
    eachHint(reconcile);
    eachPrompt(reconcilePrompt);
  });
  const applyAll = () => requestAnimationFrame(() => {
    eachHint(apply);
    eachPrompt(reconcilePrompt);
  });

  // Which entry point led into the size finder, as a Clarity tag on the session. The quiz
  // itself fires "Size Finder: Started"/"Returning" on dialog:after-show, so the tag must be
  // set synchronously on the click, before the popup finishes opening. Never let tracking
  // break the entry point.
  const tagEntry = (value) => {
    try {
      if (typeof window.clarity === 'function') window.clarity('set', 'sf_entry', value);
    } catch (error) {
      /* no tracking, no problem */
    }
  };

  // Delegated at document because the variant-picker block (and with it both the prompt and
  // the theme's size guide trigger) is replaced on every partial rerender.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-mm-prompt-open]')) {
      const prompt = target.closest(PROMPT_SELECTOR);
      const picker = prompt && getPickerFor(prompt);
      const trigger = picker && findSizeGuideTrigger(picker);
      if (!trigger) return;
      tagEntry('pdp_prompt');
      // Programmatic click -> isTrusted === false, so the handler below cannot overwrite the tag.
      trigger.click();
      return;
    }

    if (event.isTrusted && target.closest(SIZE_GUIDE_TRIGGER_SELECTOR)) {
      tagEntry('size_row');
    }
  });

  // Inside the quick-buy modal, the embedded size-chart copy is DEAD: the popup content is
  // injected via DOMParser (inline scripts of the size-finder page never execute there), and
  // the page's own quiz script is bound to the FIRST #mm-sizefinder only (duplicate id).
  // Intercept the trigger in the capture phase (before the theme's dialog delegate opens the
  // dead copy) and open the MAIN product's size-chart modal instead — that instance is fully
  // initialized. It stacks above the quick-buy modal (focus-trap pauses the underlying trap);
  // on close, the existing dialog:after-hide handler reconciles the popup hints. Without a
  // main picker the interception backs off to the default behavior.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest(SIZE_GUIDE_TRIGGER_SELECTOR);
    if (!trigger || !trigger.closest('quick-buy-modal')) return;

    const mainPicker = document.querySelector(PICKER_SELECTOR);
    const mainTrigger = mainPicker && findSizeGuideTrigger(mainPicker);
    const mainModalId = mainTrigger && mainTrigger.getAttribute('aria-controls');
    const mainModal = mainModalId && document.getElementById(mainModalId);
    if (!mainModal || typeof mainModal.show !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    tagEntry('quick_buy');
    mainModal.show();
  }, true);

  const boot = () => {
    lastProfileRaw = readProfileRaw();
    eachHint(apply);
    eachPrompt(reconcilePrompt);
  };

  // product:rerender does not bubble, but the CAPTURE phase still passes through document on
  // its way to the form — one listener here survives ANY form replacement. Crucial for the
  // quick-buy modal: its <product-rerender> has no allow-partial-rerender, so every variant
  // change replaces the ENTIRE modal content including the form. A listener bound to the form
  // element (previous approach) died with the first change and the hint never came back.
  // reconcileAll defers via requestAnimationFrame, so it runs AFTER the synchronous DOM swap.
  document.addEventListener('product:rerender', (event) => {
    const target = event.target;
    if (!target || typeof target.getAttribute !== 'function') return;
    const formId = target.getAttribute('id');
    if (!formId) return;
    let match = false;
    eachHint((hint) => {
      if (formId === hint.dataset.mmFormId) match = true;
    });
    if (match) reconcileAll();
  }, true);

  // variant:change bubbles from the product forms (main + quick buy) to document; only react
  // to forms we have a hint for. The id must be read via getAttribute: on the product form,
  // form.id is shadowed by the <input name="id"> (DOM named-property shadowing, see MEMORY.md).
  document.addEventListener('variant:change', (event) => {
    const target = event.target;
    if (!target || typeof target.getAttribute !== 'function') return;
    const formId = target.getAttribute('id');
    if (!formId) return;
    let match = false;
    eachHint((hint) => {
      if (formId === hint.dataset.mmFormId) match = true;
    });
    if (match) reconcileAll();
  });

  // Track a genuine manual size change. Programmatic radio.click() has isTrusted === false,
  // so our own preselect never marks the handle as touched.
  document.addEventListener('change', (event) => {
    if (!event.isTrusted) return;
    const radio = event.target;
    if (!radio || radio.tagName !== 'INPUT' || !radio.dataset) return;
    const position = radio.dataset.optionPosition;
    if (!position) return;
    // Match on the radio's form association, not just the option position — main product and
    // quick-buy product share the same size position, and a manual choice in one context must
    // not lock the other one.
    const radioFormId = radio.getAttribute('form') || '';
    eachHint((hint) => {
      if (position === hint.dataset.mmSizeOptionPosition && radioFormId === hint.dataset.mmFormId) {
        touchedHandles.add(hint.dataset.mmProductHandle || '');
      }
    });
  }, true);

  // A fresh quiz result — the size guide popup was closed, or mm:size-profile-updated fired.
  // Only re-preselect when the profile actually changed, so merely viewing the size guide never
  // overrides a size the customer picked manually.
  const handleProfileMaybeChanged = () => {
    const raw = readProfileRaw();
    if (raw === lastProfileRaw) {
      // Also (re)apply here: a preselect that was blocked by an open overlay (see
      // preselectBlockedByOverlay) is caught up on the size-chart close path. The guards
      // (preselectedHandles/touchedHandles) keep this idempotent — without a genuinely new
      // result nothing is overridden.
      applyAll();
      return;
    }
    lastProfileRaw = raw;
    preselectedHandles.clear();
    touchedHandles.clear();
    applyAll();
  };

  // Quick-buy modal: its content is fetched and injected fresh on every open (QuickBuyModal
  // clears it again on close). dialog:before-show fires right after injection but does not
  // bubble -> capture phase at document. Apply marker/preselect/line to the modal's hints and
  // bind product:rerender on the modal's (fresh) form.
  document.addEventListener('dialog:before-show', (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches('quick-buy-modal')) return;
    requestAnimationFrame(() => {
      const handles = [];
      modal.querySelectorAll(HINT_SELECTOR).forEach((hint) => {
        handles.push(hint.dataset.mmProductHandle || '');
        apply(hint);
      });
      modal.__mmSizeFinderHandles = handles;
    });
  }, true);

  // The size finder quiz lives inside the size guide popup (x-modal[id^="size-chart-"]).
  // dialog:after-hide fires on every close path (X, overlay, ESC) but does not bubble, so it
  // must be caught in the capture phase at document.
  document.addEventListener('dialog:after-hide', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (el.matches('x-modal[id^="size-chart-"]')) {
      handleProfileMaybeChanged();
      return;
    }
    // Quick-buy closed -> release the one-shot guards for the modal's product, so the
    // recommendation preselects again on the next open (the content is rebuilt anyway).
    // Then re-apply: a main-product preselect that was blocked while the popup was open
    // (preselectBlockedByOverlay) is caught up now. applyAll defers via requestAnimationFrame,
    // so the popup's own hints are already destroyed (QuickBuyModal clears its innerHTML on
    // this same event) and only the main hints are touched.
    if (el.matches('quick-buy-modal')) {
      if (Array.isArray(el.__mmSizeFinderHandles)) {
        el.__mmSizeFinderHandles.forEach((handle) => {
          preselectedHandles.delete(handle);
          touchedHandles.delete(handle);
        });
        el.__mmSizeFinderHandles = null;
      }
      applyAll();
    }
  }, true);

  window.addEventListener('mm:size-profile-updated', handleProfileMaybeChanged);
  document.addEventListener('mm:size-profile-updated', handleProfileMaybeChanged);

  // Cross-tab profile change -> reconcile only (no surprising selection change).
  window.addEventListener('storage', (event) => {
    if (!event || event.key === PROFILE_KEY) reconcileAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
